import { useState } from "react";
import { Download, Loader2, CreditCard, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money } from "@/lib/landingSim";
import { InvoiceTable, termsLabel } from "@/components/webadvisor/InvoiceTable";
import { SignedBadge } from "@/components/webadvisor/SignAgreement";

const List = ({ title, items }) => items?.length > 0 && (
  <div><p className="font-bold text-slate-900 mb-1.5">{title}</p>
    <ul className="space-y-1">{items.map((t, i) => <li key={i} className="text-sm text-slate-700 flex gap-2"><CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />{t}</li>)}</ul></div>
);

export const PacketSection = ({ token, packet, invoice: inv, signature, countersignature, accepted }) => {
  const [busy, setBusy] = useState(false);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webadvisor/public/proposal/${token}/packet.pdf`;
  const canPay = accepted && inv.final_invoice && inv.balance >= 0.5;
  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/api/webadvisor/public/proposal/${token}/pay-balance`, { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(err.response?.data?.detail || "Couldn't start the payment"); setBusy(false); }
  };
  return (
    <div className="space-y-5">
      {packet && (
        <div id="packet" className="scroll-mt-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="public-packet">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><p className="text-xs uppercase tracking-wider text-blue-600 font-bold">Your project packet</p><h2 className="text-xl font-black text-slate-900">Final plan, terms &amp; invoice</h2></div>
            <a href={pdfUrl} className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2.5 rounded-lg flex items-center gap-2" data-testid="public-packet-pdf-btn"><Download className="w-4 h-4" />Packet PDF</a>
          </div>
          {packet.cover_letter && <div className="text-sm text-slate-700 whitespace-pre-line leading-relaxed border-l-4 border-blue-600 pl-4" data-testid="public-packet-letter">{packet.cover_letter}</div>}
          {packet.scope_summary && <p className="text-slate-800"><b>Scope:</b> {packet.scope_summary}</p>}
          <div className="grid md:grid-cols-2 gap-5">
            <List title="Deliverables" items={packet.deliverables} />
            {packet.timeline?.length > 0 && (
              <div><p className="font-bold text-slate-900 mb-1.5">Timeline</p>
                <ol className="space-y-2">{packet.timeline.map((t, i) => <li key={i} className="text-sm"><span className="font-bold text-blue-700">{t.phase}</span><span className="text-slate-700"> — {t.detail}</span></li>)}</ol></div>
            )}
          </div>
          <List title="Terms & agreement" items={[`Payment: ${termsLabel(inv.deposit_pct)}, per invoice ${inv.invoice_number} (total ${money(inv.total)}).`, ...(packet.terms || [])]} />
          {signature ? <SignedBadge token={token} signature={signature} /> : <p className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="packet-unsigned">Not signed yet — click <b>Accept proposal</b> above to sign and lock in this plan.</p>}
          {countersignature && <SignedBadge token={token} signature={countersignature} company />}
        </div>
      )}
      <div id="invoice" className="scroll-mt-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="public-invoice">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-bold text-slate-900 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" />Invoice</p>
          <a href={`/invoice/${token}`} className="text-sm font-semibold text-blue-600 hover:underline" data-testid="public-open-invoice">Open full invoice →</a>
        </div>
        <InvoiceTable inv={inv} />
        {canPay && (
          <button onClick={pay} disabled={busy} className="w-full sm:w-auto bg-green-600 hover:bg-green-500 text-white font-bold px-6 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" data-testid="public-pay-balance-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}Pay {money(inv.balance)} balance
          </button>
        )}
        {inv.status === "paid" && <p className="text-sm font-semibold text-green-700 flex items-center gap-2" data-testid="public-paid-in-full"><CheckCircle2 className="w-4 h-4" />Paid in full — thank you!</p>}
      </div>
    </div>
  );
};
